import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import PolyLine from "@arcgis/core/geometry/Polyline";
import Polygon from "@arcgis/core/geometry/Polygon";
import {
  Feature,
  Point as point,
  LineString,
  Polygon as polygon,
} from "geojson";
import PopupTemplate from "@arcgis/core/PopupTemplate.js";
import * as projection from "@arcgis/core/geometry/projection.js";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine.js";
// handler
import { saveFileToLocalStorage } from "src/components/UI/Conversion/handler";
import SharedHandler from "src/components/UI/Shared/handler";
import user from "src/components/UI/Shared/user";
// config
import { Symbol } from "src/config/symbol";
// Data
import Store from "src/data/store";

const store = Store.getInstance();

interface Position {
  point: [longitude: number, latitude: number];
}

interface GeoJSONFeature {
  type: string;
  geometry: {
    type: string;
    coordinates: number[];
  };
  properties?: {
    [key: string]: any;
  };
}

class addGraphics {
  public static current = true;

  public static geodesicBuffer_points = (graphics: Graphic[]) => {
    const buffers = graphics.map((point) => {
      return geometryEngine.geodesicBuffer(point.geometry, 10000, "meters");
    });
    const unionGeometry: any = geometryEngine.union(buffers.flat());
    if (store.MapView) {
      this.current = false;
      store.MapView?.goTo({ target: unionGeometry.extent.expand(2) }, {});
    }
  };
  private static buffer_points = (graphics: Graphic[]) => {
    const buffers = graphics.map((point) => {
      return geometryEngine.buffer(point.geometry, 10000, "meters");
    });
    const unionGeometry: any = geometryEngine.union(buffers.flat());

    if (store.MapView) {
      this.current = false;
      store.MapView?.goTo({ target: unionGeometry.extent.expand(3) }, {});
    }
  };
  private static zoomBuffer_lines = (graphics: Graphic[]) => {
    const buffers = graphics.map((line) => {
      const polyline = line.geometry as PolyLine;
      return geometryEngine.geodesicBuffer(polyline, 10000, "meters");
    });

    const unionGeometry = geometryEngine.union(buffers.flat());

    if (store.MapView) {
      this.current = false;
      store.MapView?.goTo({ target: unionGeometry.extent.expand(3) }, {});
    }
  };

  private static zoomBuffer_polygons = (graphics: Graphic[]) => {
    const buffers = graphics.map((polygon) => {
      const polygon_ = polygon.geometry as Polygon;
      return geometryEngine.geodesicBuffer(polygon_, 10000, "meters");
    });

    const unionGeometry = geometryEngine.union(buffers.flat());

    if (store.MapView) {
      this.current = false;
      store.MapView?.goTo({ target: unionGeometry.extent.expand(3) }, {});
    }
  };

  private static createGeometry = (
    wkId: number,
    point_?: { flag: string; x: number; y: number },
    polyline_?: { path: [number, number][] },
    polygon_?: { rings: number[][][] }
  ) => {
    if (point_) {
      if (point_.flag === "geographic") {
        const point = new Point({
          longitude: point_.x,
          latitude: point_.y,
          spatialReference: new SpatialReference({
            wkid: 4326,
          }),
        });
        return point;
      } else if (point_.flag === "projected") {
        const point = new Point({
          x: point_.x,
          y: point_.y,
          spatialReference: new SpatialReference({
            wkid: wkId,
          }),
        });
        return point;
      }
    } else if (polyline_) {
      const polyline = new PolyLine({
        paths: [polyline_.path],
        spatialReference: new SpatialReference({
          wkid: wkId ? wkId : 4326,
        }),
      });
      return polyline;
    } else if (polygon_) {
      const polygon = new Polygon({
        rings: polygon_.rings,
        spatialReference: new SpatialReference({
          wkid: wkId ? wkId : 4326,
        }),
      });
      return polygon;
    } else return new Point({});
    return new Point({});
  };
  private static checkAndProject = (
    geometryType: string,
    wkId: number,
    popup: any,
    point_?: { x: number; y: number },
    polyline_?: { path: [number, number][] },
    polygon_?: { rings: number[][][] }
  ) => {
    const sr = new SpatialReference({
      wkid: wkId ? wkId : 4326,
    });
    const isGeographic = sr.isGeographic;
    if (geometryType === "point" && point_) {
      let projected_point: Point;
      let graphic: Graphic = new Graphic({});
      if (!isGeographic) {
        if (store.MapView) {
          const point = this.createGeometry(
            wkId,
            { flag: "projected", x: point_.x, y: point_.y },
            undefined,
            undefined
          );
          projected_point = projection.project(
            point,
            store.MapView.spatialReference
          ) as Point;

          graphic = new Graphic({
            geometry: projected_point,
            symbol: Symbol.point,
            popupTemplate: popup,
          });
        }
        return graphic;
      } else {
        const point = this.createGeometry(
          wkId,
          { flag: "geographic", x: point_.x, y: point_.y },
          undefined,
          undefined
        );

        graphic = new Graphic({
          geometry: point,
          symbol: Symbol.point,
          popupTemplate: popup,
        });

        return graphic;
      }
    } else if (geometryType === "polyline" && polyline_) {
      let projected_line: PolyLine;
      let graphic: Graphic = new Graphic({});
      const polyline = this.createGeometry(
        wkId,
        undefined,
        { path: polyline_.path },
        undefined
      );
      if (!isGeographic) {
        if (store.MapView) {
          projected_line = projection.project(
            polyline,
            store.MapView.spatialReference
          ) as PolyLine;
          graphic = new Graphic({
            geometry: projected_line,
            symbol: Symbol.polyline,
            popupTemplate: popup,
          });
        }
        return graphic;
      } else {
        graphic = new Graphic({
          geometry: polyline,
          symbol: Symbol.polyline,
          popupTemplate: popup,
        });

        return graphic;
      }
    } else if (geometryType === "polygon" && polygon_) {
      let projected_polygon: Polygon;
      let graphic: Graphic = new Graphic({});
      const polygon = this.createGeometry(wkId, undefined, undefined, {
        rings: polygon_.rings,
      });
      if (!isGeographic) {
        if (store.MapView) {
          projected_polygon = projection.project(
            polygon,
            store.MapView.spatialReference
          ) as Polygon;
          graphic = new Graphic({
            geometry: projected_polygon,
            symbol: Symbol.polygon,
            popupTemplate: popup,
          });
        }
        return graphic;
      } else {
        graphic = new Graphic({
          geometry: polygon,
          symbol: Symbol.polygon,
          popupTemplate: popup,
        });

        return graphic;
      }
    }
    return new Graphic({});
  };
  private static pointsGraphicsHandler = (
    points: number[][],
    pops: string[],
    popsTitles: string[],
    wkId: number,
    fileName: string,
    addToMap?: string,
    zoom?: string
  ) => {
    const graphics = points.map((pointGeometry, index) => {
      const [x, y] = pointGeometry;
      const popInfo = pops[index];
      const popup = new PopupTemplate({
        title: popsTitles[index],
        content: [
          {
            type: "text",
            text: popInfo,
          },
        ],
      });

      const graphic = this.checkAndProject(
        "point",
        wkId,
        popup,
        { x: x, y: y },
        undefined,
        undefined
      );
      return graphic;
    });

    if (addToMap && addToMap === "0") {
      return graphics;
    } else {
      store.createGraphicsLayer(fileName.split(".")[0]);
      store.graphicsLayer?.addMany(graphics);
      try {
        if (zoom && zoom === "notzoom") {
        } else this.geodesicBuffer_points(graphics);
      } catch (error) {}
    }

    return graphics;
  };

  private static linesGraphicsHandler = (
    lines: Position[][],
    pops: string[],
    popsTitles: string[],
    wkId: number,
    fileName: string,
    addToMap?: string,
    zoom?: string
  ) => {
    const graphics = lines.map((line, index) => {
      const popInfo = pops[index];
      const popup = new PopupTemplate({
        title: popsTitles[index],
        content: [
          {
            type: "text",
            text: popInfo,
          },
        ],
      });

      const coordinates: [number, number][] = line.map(
        (position: Position) => position.point
      );
      const graphic = this.checkAndProject(
        "polyline",
        wkId,
        popup,
        undefined,
        { path: coordinates },
        undefined
      );
      return graphic;
    });
    if (addToMap && addToMap === "0") {
      return graphics;
    } else {
      store.createGraphicsLayer(fileName.split(".")[0]);
      store.graphicsLayer?.addMany(graphics);
      try {
        if (zoom && zoom === "notzoom") {
        } else this.geodesicBuffer_points(graphics);
      } catch (error) {}
    }
    return graphics;
  };
  private static polygonsGraphicsHandler = (
    polygons: number[][][][],
    pops: string[],
    popsTitles: string[],
    wkId: number,
    fileName: string,
    addToMap?: string,
    zoom?: string
  ) => {
    const graphics = polygons.map((polygon, index) => {
      const popInfo = pops[index];
      const popup = new PopupTemplate({
        title: popsTitles[index],
        content: [
          {
            type: "text",
            text: popInfo,
          },
        ],
      });

      const graphic = this.checkAndProject(
        "polygon",
        wkId,
        popup,
        undefined,
        undefined,
        { rings: polygon }
      );
      return graphic;
    });

    if (addToMap && addToMap === "0") {
      return graphics;
    } else {
      store.createGraphicsLayer(fileName.split(".")[0]);
      store.graphicsLayer?.addMany(graphics);
      try {
        if (zoom && zoom === "notzoom") {
        } else this.geodesicBuffer_points(graphics);
      } catch (error) {}
    }

    return graphics;
  };
  public static projectPoints = (
    geojson_content: any,
    isGeographic: boolean,
    wkId: number,
    fileName: string,
    addToMap?: string,
    zoom?: string
  ) => {
    const points: number[][] = [];
    let graphics: Graphic[] = [];
    const pops: string[] = [];
    const popsTitles: string[] = [];

    geojson_content.features.map((feature: Feature<point>) => {
      if (
        SharedHandler.checkValidCoordinates(feature["geometry"]["coordinates"])
      ) {
        points.push(feature["geometry"]["coordinates"]);
        let popupInfo_;
        if (feature["properties"]) {
          popupInfo_ = feature["properties"]["PopupInfo"]
            ? feature["properties"]["PopupInfo"]
            : feature["properties"]
            ? feature["properties"]
            : "";
        }
        let popupInfo = "";
        if (popupInfo_ !== "") {
          const keys = Object.keys(popupInfo_);
          const values = Object.values(popupInfo_);
          popupInfo = `<table style="border-collapse:collapse; border:1px solid black;"">`;
          keys.map((property, ind) => {
            popupInfo += `<tr><td style="font-weight:bold; border:1px solid black;width:50%;padding:6px;">${property}</td><td style="border:1px solid black;width:50%;padding:6px;">${values[ind]}</td></tr>`;
          });
          popupInfo += "</table>";
        }

        pops.push(popupInfo);
        popsTitles.push(popupInfo_["Name"] || "Point");
      }
    });
    if (!isGeographic && !projection.isLoaded()) {
      projection.load().then(() => {
        graphics = this.pointsGraphicsHandler(
          points,
          pops,
          popsTitles,
          wkId,
          fileName,
          addToMap,
          zoom
        );
      });
    } else {
      graphics = this.pointsGraphicsHandler(
        points,
        pops,
        popsTitles,
        wkId,
        fileName,
        addToMap,
        zoom
      );
    }
    return graphics;
  };
  public static projectPolygons = (
    geojson_content: any,
    isGeographic: boolean,
    wkId: number,
    fileName: string,
    addToMap?: string,
    zoom?: string
  ) => {
    let polygons: any[][][] = [];
    const pops: string[] = [];
    const popsTitles: string[] = [];
    let graphics: Graphic[] = [];

    geojson_content.features.map((feature: Feature<polygon>) => {
      if (SharedHandler.checkValidCoordinates(feature.geometry.coordinates)) {
        polygons.push(feature.geometry.coordinates);
        let popupInfo_;
        if (feature["properties"]) {
          popupInfo_ = feature["properties"]["PopupInfo"]
            ? feature["properties"]["PopupInfo"]
            : feature["properties"]
            ? feature["properties"]
            : "";
        }
        let popupInfo = "";
        if (popupInfo_ !== "") {
          const keys = Object.keys(popupInfo_);
          const values = Object.values(popupInfo_);
          popupInfo = `<table style="border-collapse:collapse; border:1px solid black;"">`;
          keys.map((property, ind) => {
            popupInfo += `<tr><td style="font-weight:bold; border:1px solid black;width:50%;padding:6px;">${property}</td><td style="border:1px solid black;width:50%;padding:6px;">${values[ind]}</td></tr>`;
          });
          popupInfo += "</table>";
        }

        pops.push(popupInfo);
        popsTitles.push(popupInfo_["Name"] || "Polygon");
      }
    });

    if (!isGeographic && !projection.isLoaded()) {
      projection.load().then(() => {
        graphics = this.polygonsGraphicsHandler(
          polygons,
          pops,
          popsTitles,
          wkId,
          fileName,
          addToMap,
          zoom
        );
      });
    } else {
      graphics = this.polygonsGraphicsHandler(
        polygons,
        pops,
        popsTitles,
        wkId,
        fileName,
        addToMap,
        zoom
      );
    }
    return graphics;
  };
  public static projectLines = (
    geojson_content: any,
    isGeographic: boolean,
    wkId: number,
    fileName: string,
    addToMap?: string,
    zoom?: string
  ) => {
    let lines: Position[][] = [];
    let graphics: Graphic[] = [];
    const pops: string[] = [];
    const popsTitles: string[] = [];

    geojson_content.features.map((feature: Feature<LineString>) => {
      if (SharedHandler.checkValidCoordinates(feature.geometry.coordinates)) {
        const coordinates: Position[] = feature.geometry.coordinates.map(
          ([longitude, latitude]) => ({
            point: [longitude, latitude],
          })
        );
        lines.push(coordinates);
        let popupInfo_;
        if (feature["properties"]) {
          popupInfo_ = feature["properties"]["PopupInfo"]
            ? feature["properties"]["PopupInfo"]
            : feature["properties"]
            ? feature["properties"]
            : "";
        }
        let popupInfo = "";
        if (popupInfo_ !== "") {
          const keys = Object.keys(popupInfo_);
          const values = Object.values(popupInfo_);
          popupInfo = `<table style="border-collapse:collapse; border:1px solid black;"">`;
          keys.map((property, ind) => {
            popupInfo += `<tr><td style="font-weight:bold; border:1px solid black;width:50%;padding:6px;">${property}</td><td style="border:1px solid black;width:50%;padding:6px;">${values[ind]}</td></tr>`;
          });
          popupInfo += "</table>";
        }

        pops.push(popupInfo);
        popsTitles.push(popupInfo_["Name"] || "Line");
      }
    });

    if (!isGeographic && !projection.isLoaded()) {
      projection.load().then(() => {
        graphics = this.linesGraphicsHandler(
          lines,
          pops,
          popsTitles,
          wkId,
          fileName,
          addToMap,
          zoom
        );
      });
    } else {
      graphics = this.linesGraphicsHandler(
        lines,
        pops,
        popsTitles,
        wkId,
        fileName,
        addToMap,
        zoom
      );
    }
    return graphics;
  };
  private static handleGeometries = (
    geojson_content: any,
    fileName: string,
    zoom?: string
  ) => {
    const contentType = geojson_content.type;
    const geomType = geojson_content["features"][0]["geometry"]["type"];
    let wkId: number = 0;

    if (geojson_content.crs) {
      const crs = geojson_content.crs["properties"]["name"];
      wkId = Number(crs.match(/\d+/)[0]);
    } else {
      wkId = 4326;
    }
    const sr = new SpatialReference({
      wkid: wkId !== 0 ? wkId : 4326,
    });
    const isGeographic = sr.isGeographic;
    if (contentType === "FeatureCollection" && geomType === "Point") {
      this.projectPoints(
        geojson_content,
        isGeographic,
        wkId,
        fileName,
        undefined,
        zoom
      );
    } else if (
      contentType === "FeatureCollection" &&
      geomType === "LineString"
    ) {
      this.projectLines(
        geojson_content,
        isGeographic,
        wkId,
        fileName,
        undefined,
        zoom
      );
    } else if (contentType === "FeatureCollection" && geomType === "Polygon") {
      this.projectPolygons(
        geojson_content,
        isGeographic,
        wkId,
        fileName,
        undefined,
        zoom
      );
    }
  };
  public static addFromGeojsonData = async (
    geometries: any,
    fileName: string,
    zoom?: string
  ) => {
    addGraphics.handleGeometries(geometries, fileName, zoom);
  };
  private static handleJson = (json_content: any, fileName: string) => {
    const geomType = json_content.geometryType;
    const points: number[][] = [];
    let lines: Position[][] = [];
    let polygons: number[][][][] = [];
    const pops: string[] = [];
    const popsTitles: string[] = [];
    let flag = "canZoom";
    const wkId = json_content.spatialReference.wkid;
    const sr = new SpatialReference({
      wkid: wkId ? wkId : 4326,
    });
    const isGeographic = sr.isGeographic;
    if (geomType === "esriGeometryPoint") {
      json_content.features.map((feature: any, ind: number) => {
        if (
          SharedHandler.checkValidCoordinates([
            feature["geometry"]["x"],
            feature["geometry"]["y"],
          ])
        ) {
          points.push([feature["geometry"]["x"], feature["geometry"]["y"]]);
          let popupInfo_;
          if (feature["attributes"]) {
            popupInfo_ = feature["attributes"] ? feature["attributes"] : "";
          }
          let popupInfo = "";
          if (popupInfo_ !== "") {
            const keys = Object.keys(popupInfo_);
            const values = Object.values(popupInfo_);
            popupInfo = `<table style="border-collapse:collapse; border:1px solid black;"">`;
            keys.map((property, ind) => {
              popupInfo += `<tr><td style="font-weight:bold; border:1px solid black;width:50%;padding:6px;">${property}</td><td style="border:1px solid black;width:50%;padding:6px;">${values[ind]}</td></tr>`;
            });
            popupInfo += "</table>";
          }

          pops.push(popupInfo);
          popsTitles.push(popupInfo_["Name"] || "Point");
        }
      });

      if (!isGeographic && !projection.isLoaded()) {
        projection.load().then(() => {
          const graphics = this.pointsGraphicsHandler(
            points,
            pops,
            popsTitles,
            wkId,
            fileName,
            undefined,
            "notzoom"
          );
          try {
            this.geodesicBuffer_points(graphics);
          } catch (error) {
            flag = "cannotZoom";
          }
        });
      } else {
        const graphics = this.pointsGraphicsHandler(
          points,
          pops,
          popsTitles,
          wkId,
          fileName,
          undefined,
          "notzoom"
        );

        try {
          addGraphics.geodesicBuffer_points(graphics);
        } catch (error) {
          flag = "cannotZoom";
        }
      }
    } else if (geomType === "esriGeometryPolyline") {
      json_content.features.map((feature: any) => {
        if (SharedHandler.checkValidCoordinates(feature.geometry.paths)) {
          const line = feature.geometry as PolyLine;
          const coordinates: Position[] = line.paths[0].map(
            ([longitude, latitude]) => ({
              point: [longitude, latitude],
            })
          );
          lines.push(coordinates);
          let popupInfo_;
          if (feature["attributes"]) {
            popupInfo_ = feature["attributes"] ? feature["attributes"] : "";
          }
          let popupInfo = "";
          if (popupInfo_ !== "") {
            const keys = Object.keys(popupInfo_);
            const values = Object.values(popupInfo_);
            popupInfo = `<table style="border-collapse:collapse; border:1px solid black;"">`;
            keys.map((property, ind) => {
              popupInfo += `<tr><td style="font-weight:bold; border:1px solid black;width:50%;padding:6px;">${property}</td><td style="border:1px solid black;width:50%;padding:6px;">${values[ind]}</td></tr>`;
            });
            popupInfo += "</table>";
          }

          pops.push(popupInfo);
          popsTitles.push(popupInfo_["Name"] || "Line");
        }
      });

      if (!isGeographic && !projection.isLoaded()) {
        projection.load().then(() => {
          const graphics = this.linesGraphicsHandler(
            lines,
            pops,
            popsTitles,
            wkId,
            fileName,
            undefined,
            "notzoom"
          );

          try {
            addGraphics.geodesicBuffer_points(graphics);
          } catch (error) {
            flag = "cannotZoom";
          }
        });
      } else {
        const graphics = this.linesGraphicsHandler(
          lines,
          pops,
          popsTitles,
          wkId,
          fileName,
          undefined,
          "notzoom"
        );

        try {
          addGraphics.geodesicBuffer_points(graphics);
        } catch (error) {
          flag = "cannotZoom";
        }
      }
    } else if (geomType === "esriGeometryPolygon") {
      json_content.features.map((feature: any) => {
        if (SharedHandler.checkValidCoordinates(feature.geometry.rings)) {
          polygons.push(feature.geometry.rings[0]);
          let popupInfo_;
          if (feature["attributes"]) {
            popupInfo_ = feature["attributes"] ? feature["attributes"] : "";
          }
          let popupInfo = "";
          if (popupInfo_ !== "") {
            const keys = Object.keys(popupInfo_);
            const values = Object.values(popupInfo_);
            popupInfo = `<table style="border-collapse:collapse; border:1px solid black;"">`;
            keys.map((property, ind) => {
              popupInfo += `<tr><td style="font-weight:bold; border:1px solid black;width:50%;padding:6px;">${property}</td><td style="border:1px solid black;width:50%;padding:6px;">${values[ind]}</td></tr>`;
            });
            popupInfo += "</table>";
          }

          pops.push(popupInfo);
          popsTitles.push(popupInfo_["Name"] || "Polygon");
        }
      });

      if (!isGeographic && !projection.isLoaded()) {
        projection.load().then(() => {
          const graphics = this.polygonsGraphicsHandler(
            polygons,
            pops,
            popsTitles,
            wkId,
            fileName,
            undefined,
            "notzoom"
          );
          try {
            addGraphics.geodesicBuffer_points(graphics);
          } catch (error) {
            flag = "cannotZoom";
          }
        });
      } else {
        const graphics = this.polygonsGraphicsHandler(
          polygons,
          pops,
          popsTitles,
          wkId,
          fileName,
          undefined,
          "notzoom"
        );

        try {
          addGraphics.geodesicBuffer_points(graphics);
        } catch (error) {
          flag = "cannotZoom";
        }
      }
    }
    return flag;
  };
  public static addFromJsonData = (geometries: any, fileName: string) => {
    const flag = addGraphics.handleJson(geometries, fileName);

    return flag;
  };
  public static addFromSavedGeojsonData = (fileName: string) => {
    const current_user = user.getCurrentLoggedIn();
    const data = localStorage.getItem(current_user);
    if (data) {
      const user_data = JSON.parse(data);
      const files = user_data["files"];
      const item = files[fileName];
      const geojson_content = item["data"];
      const ref = item["ref"].startsWith("GISimplify_")
        ? item["ref"].substring(11)
        : item["ref"];
      addGraphics.handleGeometries(geojson_content, ref);
    }
  };

  public static addFromRows = (
    selectFields: number[],
    rows: string[],
    fileName: string,
    saveLocally: boolean,
    flag: string = "1",
    startFrom: number = 1
  ) => {
    const latIndex = selectFields[0] - 1;
    const longIndex = selectFields[1] - 1;

    let fields: any = [];

    if (latIndex == 0) fields = ["Lat", "Long"];
    else fields = ["Long", "Lat"];
    const features: GeoJSONFeature[] = [];
    let geojson_obj;
    const pointsGraphics: Graphic[] = rows
      .slice(startFrom, rows.length)
      .map((row) => {
        const values = row.split(",");
        const latitude = parseFloat(values[latIndex]);
        const longitude = parseFloat(values[longIndex]);

        const feature = {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [longitude, latitude],
          },
          properties: {},
        };
        features.push(feature);

        const point = new Point({
          longitude: longitude,
          latitude: latitude,
          spatialReference: store.MapView?.spatialReference,
        });

        const attributes: any = {};
        fields.forEach((field: string, index: number) => {
          attributes[field] = values[index];
        });
        const fieldInfos = fields.map((x: any) => {
          return { fieldName: x };
        });
        const popup = {
          title: fileName,
          content: [
            {
              type: "fields",
              fieldInfos: fieldInfos,
            },
          ],
        };

        return new Graphic({
          geometry: point,
          symbol: Symbol.point,
          popupTemplate: popup,
          attributes: attributes,
        });
      });
    geojson_obj = {
      crs: {
        type: "name",
        properties: {
          name: "EPSG:4326",
        },
      },
      type: "FeatureCollection",
      features: features,
    };
    if (saveLocally) {
      saveFileToLocalStorage(fileName, geojson_obj);
    }
    if (flag === "1") {
      store.createGraphicsLayer(fileName.split(".")[0]);
      store.graphicsLayer?.addMany(pointsGraphics);

      try {
        addGraphics.geodesicBuffer_points(pointsGraphics);
      } catch (error) {}
    }
    return geojson_obj;
  };
}

export default addGraphics;
